import { Machine, sendParent, send, assign } from "xstate";
import { DialogContentText } from "@material-ui/core";

const validEmailRegex = /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/g;
const validPhoneRegex = /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

const email = {
  initial: "noError",
  states: {
    noError: {
      on: {
        SUBMIT: [
          {
            cond: "isEmailEmpty",
            target: "error.empty"
          },
          {
            cond: "isEmailBadFormat",
            target: "error.badFormat"
          }
        ]
      }
    },
    error: {
      initial: "empty",
      states: {
        empty: {},
        badFormat: {}
      }
    }
  }
};

const confirmEmail = {
  initial: "noError",
  states: {
    noError: {
      on: {
        SUBMIT: [
          {
            cond: "doEmailsMismatch",
            target: "error.mustMatch"
          }
        ]
      }
    },
    error: {
      initial: "mustMatch",
      states: {
        mustMatch: {}
      }
    }
  }
};

const firstName = {
  initial: "noError",
  states: {
    noError: {
      on: {
        SUBMIT: [
          {
            cond: "isFirstnameEmpty",
            target: "error.empty"
          }
        ]
      }
    },
    error: {
      initial: "empty",
      states: {
        empty: {}
      }
    }
  }
};

const lastName = {
  initial: "noError",
  states: {
    noError: {
      on: {
        SUBMIT: [
          {
            cond: "isLastnameEmpty",
            target: "error.empty"
          }
        ]
      }
    },
    error: {
      initial: "empty",
      states: {
        empty: {}
      }
    }
  }
};

const phoneNumber = {
  initial: "noError",
  states: {
    noError: {
      on: {
        SUBMIT: [
          {
            cond: "isPhoneEmpty",
            target: "error.empty"
          },
          {
            cond: "isPhoneFormatBad",
            target: "error.badFormat"
          }
        ]
      }
    },
    error: {
      initial: "empty",
      states: {
        empty: {},
        badFormat: {}
      }
    }
  }
};

export const getStep1 = (values: any) => {
  const defaultValues = {
    firstName: "",
    lastName: "",
    phoneNumber: null,
    email: "",
    confirmEmail: ""
  };

  const config = {
    id: "step1",
    initial: "ready",
    context: {
      ...defaultValues,
      ...values
    },
    states: {
      ready: {
        type: "parallel",
        on: {
          INPUT_EMAIL: {
            actions: "setEmail",
            target: ["ready.email.noError", "ready.confirmEmail.noError"]
          },
          INPUT_CONFIRM_EMAIL: {
            actions: "setConfirmEmail",
            target: ["ready.email.noError", "ready.confirmEmail.noError"]
          },
          INPUT_FIRST_NAME: {
            actions: "setFirstName",
            target: "ready.firstName.noError"
          },
          INPUT_LAST_NAME: {
            actions: "setLastName",
            target: "ready.lastName.noError"
          },
          INPUT_PHONE: {
            actions: "setPhone",
            target: "ready.phoneNumber.noError"
          },
          SUBMIT: {
            cond: (_0, _1, meta) => {
              if (
                meta.state.matches("ready.email.error") ||
                meta.state.matches("ready.confirmEmail.error") ||
                meta.state.matches("ready.firstName.error") ||
                meta.state.matches("ready.lastName.error") ||
                meta.state.matches("ready.phoneNumber.error")
              ) {
                return false;
              }

              return true;
            },
            target: "finished"
          }
        },
        states: {
          email: {
            ...email
          },
          confirmEmail: {
            ...confirmEmail
          },
          firstName: {
            ...firstName
          },
          lastName: {
            ...lastName
          },
          phoneNumber: {
            ...phoneNumber
          }
        }
      },
      noError: {},
      finished: {
        type: "final"
      },
      error: {
        type: "parallel",
        email: { ...email },
        confirmEmail: { ...confirmEmail },
        firstName: { ...firstName },
        lastName: { ...lastName },
        phoneNumber: { ...phoneNumber }
      },
      submitted: { onEntry: "complete" }
    }
  };

  function isEmpty(someString: string) {
    if (!someString || someString.length === 0) {
      return true;
    }

    return false;
  }

  const step1 = Machine(config as any, {
    actions: {
      setEmail: assign((context: any, event: any) => ({
        email: event.email
      })),
      setConfirmEmail: assign((context: any, event: any) => ({
        confirmEmail: event.confirmEmail
      })),
      setFirstName: assign((context: any, event: any) => ({
        firstName: event.firstName
      })),
      setLastName: assign((context: any, event: any) => ({
        lastName: event.lastName
      })),
      setPhone: assign((context: any, event: any) => ({
        phoneNumber: event.phoneNumber
      }))
    },
    guards: {
      isEmailEmpty: (context: any, event: any) => {
        return isEmpty(context.email);
      },
      isEmailBadFormat: (context: any, event: any) => {
        const isValid = validEmailRegex.test(context.email);
        validEmailRegex.test("gibberish to reset the test 🙃");

        return !isValid;
      },
      doEmailsMismatch: (context: any, event: any) => {
        const isMatch = context.email === context.confirmEmail;

        return !isMatch;
      },
      isFirstnameEmpty: (context: any, event: any) => {
        return isEmpty(context.firstName);
      },
      isLastnameEmpty: (context: any, event: any) => {
        return isEmpty(context.lastName);
      },
      isPhoneEmpty: (context: any, event: any) => {
        return isEmpty(context.phoneNumber);
      },
      isPhoneFormatBad: (context: any, event: any) => {
        return validPhoneRegex.test(context.phoneNumber);
      }
    }
  });

  return step1;
};
